import React, { useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  AudioWaveform,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Grid3X3,
  Search,
  Sparkles,
  Waves,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { cn } from "../lib/utils";
import {
  SECTIONS,
  SIGNAL_FLOW,
  FLOW_INTRO,
  CONTROLS,
  OSC_TYPES,
  OSC_AVAIL_LABELS,
  FX_TYPES,
  FX_ROUTING_NOTE,
  ARP_MODES,
  ARP_MODIFIERS,
  SEQ_STEP_RECORDING,
  SEQ_EDIT_TRICKS,
  GROOVE_TOOLS,
  SEQ_MOD_LANES_NOTE,
  RECIPES,
  GOTCHAS,
  GLOSSARY,
} from "../lib/minifreakGuide";

/* ============================================================
   MiniFreak — a friendly field guide to the Arturia MiniFreak
   for someone new to the machine and to sound design. Unlisted
   page; content lives in src/lib/minifreakGuide.js.
   ============================================================ */

/* ---- small shared bits ---- */

const SubHeading = ({ title, blurb }) => (
  <div className="mt-8">
    <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
    {blurb && (
      <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{blurb}</p>
    )}
  </div>
);

const SectionHeading = ({ id, icon: Icon, title, blurb }) => (
  <div id={id} className="scroll-mt-36 pt-12">
    <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
      {Icon && <Icon className="h-6 w-6 text-primary" />}
      {title}
    </h2>
    {blurb && <p className="mt-2 max-w-3xl text-muted-foreground">{blurb}</p>}
  </div>
);

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
    {children}
  </span>
);

/* ---- sticky section nav ---- */

// top-16 keeps this just below the site's sticky navbar (h-16).
const SectionNav = () => (
  <nav className="sticky top-16 z-20 -mx-4 border-b border-border bg-background/90 px-4 py-2 backdrop-blur">
    <div className="mx-auto flex max-w-5xl gap-1.5 overflow-x-auto whitespace-nowrap py-1 text-sm [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {SECTIONS.map((s) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          className="rounded-full border border-transparent px-3 py-1 text-muted-foreground transition-colors hover:border-border hover:bg-accent hover:text-foreground"
        >
          {s.label}
        </a>
      ))}
    </div>
  </nav>
);

/* ---- signal flow ---- */

const SignalFlow = () => (
  <div className="mt-6 grid gap-3 md:grid-cols-4">
    {SIGNAL_FLOW.map((stage, i) => (
      <div key={stage.name} className="relative">
        <Card className="h-full bg-card/80">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wide">
              Step {i + 1}
            </CardDescription>
            <CardTitle className="text-base">{stage.name}</CardTitle>
            <CardDescription className="font-medium text-primary">
              {stage.role}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {stage.detail}
          </CardContent>
        </Card>
        {i < SIGNAL_FLOW.length - 1 && (
          <ArrowRight className="absolute -right-3 top-1/2 z-10 hidden h-5 w-5 -translate-y-1/2 text-primary md:block" />
        )}
      </div>
    ))}
  </div>
);

/* ---- knob finder ---- */

const KnobFinder = () => {
  const [query, setQuery] = useState("");
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return CONTROLS;
    return CONTROLS.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.what.toLowerCase().includes(q) ||
        c.section.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <Card className="mt-6 bg-card/80">
      <CardHeader className="pb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Try "cutoff", "shift", "timbre", "spice"…'
            className="w-full rounded-lg border border-input bg-background py-2 pl-9 pr-3 text-sm outline-none ring-primary/40 placeholder:text-muted-foreground focus:ring-2"
          />
        </div>
      </CardHeader>
      <CardContent>
        {results.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            Nothing matches — try a shorter word, like "env" or "wave".
          </p>
        ) : (
          <ul className="grid gap-2 md:grid-cols-2">
            {results.map((c) => (
              <li
                key={`${c.section}-${c.name}`}
                className="rounded-lg border border-border bg-background/60 p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">{c.name}</span>
                  <Chip>{c.section}</Chip>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{c.what}</p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

/* ---- oscillator engine browser ---- */

const OSC_FILTERS = [
  { key: "all", label: "All engines" },
  { key: "both", label: "Osc 1 & 2" },
  { key: "osc1", label: "Osc 1 only" },
  { key: "osc2", label: "Osc 2 only" },
  { key: "proc", label: "Osc 2 processors" },
];

const OscTypeCard = ({ type }) => {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      className="w-full rounded-lg border border-border bg-card/70 p-3 text-left transition-colors hover:border-primary/50"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold">{type.name}</span>
            <Chip>{OSC_AVAIL_LABELS[type.avail]}</Chip>
          </div>
          <p className="mt-0.5 text-sm font-medium text-primary">{type.vibe}</p>
        </div>
        {open ? (
          <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </div>
      {open && (
        <div className="mt-2 space-y-2 text-sm text-muted-foreground">
          <p>{type.desc}</p>
          <ul className="space-y-1 rounded-md bg-muted/60 p-2">
            <li>
              <span className="font-semibold text-foreground">Wave:</span>{" "}
              {type.knobs.wave}
            </li>
            <li>
              <span className="font-semibold text-foreground">Timbre:</span>{" "}
              {type.knobs.timbre}
            </li>
            <li>
              <span className="font-semibold text-foreground">Shape:</span>{" "}
              {type.knobs.shape}
            </li>
          </ul>
        </div>
      )}
    </button>
  );
};

const OscBrowser = () => {
  const [filter, setFilter] = useState("all");
  const shown =
    filter === "all" ? OSC_TYPES : OSC_TYPES.filter((t) => t.avail === filter);
  return (
    <div className="mt-6">
      <div className="flex flex-wrap gap-1.5">
        {OSC_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              filter === f.key
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card text-muted-foreground hover:bg-accent"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {shown.map((t) => (
          <OscTypeCard key={t.name} type={t} />
        ))}
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        Tap an engine to see what its three knobs do. The "processor" types are
        special: instead of making their own sound, Osc 2 chews on whatever Osc
        1 is doing — extra filters, phasers, and outright destruction inside a
        single voice.
      </p>
    </div>
  );
};

/* ---- ADSR diagram ---- */

const AdsrDiagram = () => (
  <svg
    viewBox="0 0 320 120"
    className="mt-2 w-full max-w-md"
    role="img"
    aria-label="ADSR envelope shape: attack rises, decay falls to sustain level, release falls to zero"
  >
    <polyline
      points="10,110 70,15 130,55 220,55 300,110"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      className="text-primary"
    />
    <line x1="10" y1="110" x2="310" y2="110" stroke="currentColor" strokeWidth="1" className="text-border" />
    {[
      ["A", 40], ["D", 100], ["S", 175], ["R", 260],
    ].map(([label, x]) => (
      <text
        key={label}
        x={x}
        y={104}
        textAnchor="middle"
        className="fill-current text-muted-foreground"
        fontSize="12"
        fontWeight="600"
      >
        {label}
      </text>
    ))}
  </svg>
);

/* ---- generic list sections ---- */

const RecipeCard = ({ recipe }) => (
  <Card className="bg-card/80">
    <CardHeader className="pb-2">
      <CardTitle className="text-base">{recipe.name}</CardTitle>
      <CardDescription className="font-medium text-primary">
        {recipe.vibe}
      </CardDescription>
    </CardHeader>
    <CardContent>
      <ol className="list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
        {recipe.steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>
    </CardContent>
  </Card>
);

const MiniFreak = () => (
  <div className="min-h-screen bg-background px-4 pb-20 text-foreground">
    <div className="mx-auto max-w-5xl">
      {/* ---- header ---- */}
      <header className="pb-4 pt-10">
        <p className="text-sm font-medium uppercase tracking-widest text-primary">
          Field guide
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">
          Learning the MiniFreak
        </h1>
        <p className="mt-3 max-w-3xl text-muted-foreground">
          A friendlier map of the Arturia MiniFreak for someone new to the
          machine (and to sound design). Skip the 112-page manual: what each
          knob does, why you'd care, and recipes for your first sounds. Keep
          the synth in front of you and twist along.
        </p>
      </header>

      <SectionNav />

      {/* ---- big picture ---- */}
      <SectionHeading
        id="big-picture"
        icon={Waves}
        title="The big picture"
        blurb={FLOW_INTRO}
      />
      <SignalFlow />
      <Card className="mt-4 border-primary/30 bg-card/80">
        <CardContent className="py-4 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">
            The one experiment that teaches the most:
          </span>{" "}
          load any preset, hold a note, and slowly sweep the big Cutoff knob
          down and back up. Then do the same with Resonance halfway up. That's
          subtractive synthesis — everything else on the panel is a variation
          on "something turns a knob for you."
        </CardContent>
      </Card>

      {/* ---- knob finder ---- */}
      <SectionHeading
        id="knob-finder"
        icon={Search}
        title="Knob finder"
        blurb='"What does this thing do?" — search any control on the panel. Blue labels on the synth mean: hold Shift.'
      />
      <KnobFinder />

      {/* ---- oscillators ---- */}
      <SectionHeading
        id="oscillators"
        icon={AudioWaveform}
        title="Oscillators — where sound is born"
        blurb="Two digital oscillators make the raw sound. Pick an engine with Type, then sculpt it with Wave, Timbre, and Shape — three knobs that mean something different for every engine (the screen always tells you). This is easily half the fun of the MiniFreak."
      />
      <OscBrowser />

      {/* ---- filter ---- */}
      <SectionHeading
        id="filter"
        icon={Sparkles}
        title="The analog filter — where sound gets shaped"
        blurb="Everything the oscillators make flows through a genuinely analog filter (inspired by the classic Oberheim SEM). It removes frequencies — that's it — but it's where most of a synth's character and movement comes from."
      />
      <div className="mt-6 grid gap-3 md:grid-cols-3">
        {[
          {
            t: "Low Pass (LP)",
            d: "Keeps the lows, cuts the highs. The default choice — turning Cutoff down makes things dark and muffled, like a sound behind a door.",
          },
          {
            t: "Band Pass (BP)",
            d: "Keeps only a middle slice. Thin, nasal, focused — great for squelchy or radio-like sounds.",
          },
          {
            t: "High Pass (HP)",
            d: "Keeps the highs, cuts the lows. Makes things thin and airy; handy for effects or making room in a mix.",
          },
        ].map((f) => (
          <Card key={f.t} className="bg-card/80">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{f.t}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              {f.d}
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="mt-4 space-y-3 max-w-3xl text-sm text-muted-foreground">
        <p>
          <span className="font-semibold text-foreground">Cutoff</span> sets
          where the filter starts cutting (about 20 Hz–20 kHz).{" "}
          <span className="font-semibold text-foreground">Resonance</span>{" "}
          boosts a peak right at that point — a little adds presence, a lot
          gets squelchy and aggressive, and at maximum the filter
          self-oscillates and whistles a pure tone with no input at all.
        </p>
        <p>
          The <span className="font-semibold text-foreground">Env/Velo</span>{" "}
          knob is the secret third ingredient: it lets the envelope push the
          cutoff around on every note (center = off, and it works in both
          directions). This is how you get notes that start bright and sweep
          dark — the signature synth move. Shift+turn it and key velocity
          scales that effect, so playing harder literally sounds brighter.
        </p>
      </div>

      {/* ---- envelopes ---- */}
      <SectionHeading
        id="envelopes"
        icon={BookOpen}
        title="Envelopes — giving notes a shape"
        blurb="Without an envelope, a note would just switch on and drone. The envelope draws the volume (and anything else you route it to) over the life of each note."
      />
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Card className="bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">The main Envelope (ADSR)</CardTitle>
            <CardDescription>
              Hard-wired to volume; can also drive the filter and anything in
              the Matrix.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            <AdsrDiagram />
            <ul className="mt-3 space-y-1.5">
              <li>
                <b className="text-foreground">Attack</b> — time to reach full
                volume. Instant = pluck; slow = swell.
              </li>
              <li>
                <b className="text-foreground">Decay</b> — time to fall from
                the peak to the sustain level.
              </li>
              <li>
                <b className="text-foreground">Sustain</b> — a{" "}
                <em>level</em>, not a time: where a held note sits.
              </li>
              <li>
                <b className="text-foreground">Release</b> — how long sound
                lingers after you let go.
              </li>
            </ul>
            <p className="mt-3">
              Two shapes to memorize: <b className="text-foreground">pluck</b>{" "}
              (A=0, short D, S=0, short R) and{" "}
              <b className="text-foreground">pad</b> (slow A, full S, long R).
              Most sounds are somewhere between those two.
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">The Cycling Envelope</CardTitle>
            <CardDescription>
              A second envelope that can loop — half envelope, half LFO.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              Three knobs — <b className="text-foreground">Rise</b>,{" "}
              <b className="text-foreground">Fall</b>, and{" "}
              <b className="text-foreground">Hold/Sustain</b> — and a Mode
              button with three personalities:
            </p>
            <ul className="space-y-1.5">
              <li>
                <b className="text-foreground">Env</b> — behaves like a second
                one-shot envelope for modulating things (filter, timbre…).
              </li>
              <li>
                <b className="text-foreground">Run</b> — loops freely on its
                own, the same for all voices.
              </li>
              <li>
                <b className="text-foreground">Loop</b> — loops per note,
                retriggering with each key — like an LFO you can shape.
              </li>
            </ul>
            <p>
              It does nothing until you route it somewhere in the Matrix — its
              row is the very first one, top left.
            </p>
          </CardContent>
        </Card>
      </div>
      <Card className="mt-4 bg-card/80">
        <CardContent className="py-4 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">
            The voice Mode button
          </span>{" "}
          lives here too: <b className="text-foreground">Mono</b> (one note,
          great for bass/leads), <b className="text-foreground">Poly</b>{" "}
          (chords, 6 voices), <b className="text-foreground">Para</b> (12
          thinner voices, Osc 2 turns off), and{" "}
          <b className="text-foreground">Uni</b> (voices stacked and detuned on
          each note — huge; tune the spread in Sound Edit &gt; Voice).
        </CardContent>
      </Card>

      {/* ---- LFOs ---- */}
      <SectionHeading
        id="lfos"
        icon={Waves}
        title="LFOs — automatic knob-wigglers"
        blurb="An LFO is a slow wave you never hear directly; it exists to move other controls for you. Vibrato is an LFO on pitch, wobble bass is an LFO on the filter, PWM is an LFO on a square wave's width."
      />
      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <Card className="bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">The three knobs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              <b className="text-foreground">Select/Edit</b> picks LFO 1 or 2.{" "}
              <b className="text-foreground">Rate</b> sets speed — click it to
              lock to the tempo in musical divisions (do this; synced wobbles
              instantly sound intentional).{" "}
              <b className="text-foreground">Wave</b> picks the shape.
            </p>
            <p>
              Shapes in plain words: <b className="text-foreground">sine/tri</b>{" "}
              = smooth back-and-forth, <b className="text-foreground">square</b>{" "}
              = on/off trill, <b className="text-foreground">saw</b> = repeated
              ramps, <b className="text-foreground">S&amp;H</b> = random jumps
              (bleep-bloop computer).
            </p>
            <p>
              Shift+turn Rate picks <em>triggering</em> — whether the LFO
              restarts on every note, runs free, or fires once like a mini
              envelope.
            </p>
          </CardContent>
        </Card>
        <Card className="bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Draw your own (Shaper)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              Shift+click Select/Edit and the 16 step pads become a drawing
              grid: set each step's height with the Bend strip, its slope with
              the first four pattern pads, its curve with the Wheel strip. Up
              to 16 steps, savable to a library.
            </p>
            <p>
              There's also a hidden third LFO —{" "}
              <b className="text-foreground">Vibrato</b> — dedicated to pitch
              wobble on the Wheel strip. Shift+touch the Bend/Wheel pad to
              toggle it.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ---- matrix ---- */}
      <SectionHeading
        id="matrix"
        icon={Grid3X3}
        title="The Mod Matrix — the patch bay"
        blurb="The grid at the top-left is where sounds come alive: connect a source (rows) to a destination (columns), set an amount, done. A lit LED means that connection is active."
      />
      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <Card className="bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">How to make a connection</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            <ol className="list-decimal space-y-1.5 pl-5">
              <li>Turn the Matrix encoder to walk the grid to your crossing point (e.g. LFO1 row × Cutoff column).</li>
              <li>Click it — now you're setting the Amount. Amounts can be negative (modulation pushes down instead of up).</li>
              <li>Turn to taste while holding a note, click again to lock it in.</li>
              <li>Hold the encoder for a second to zero a connection out.</li>
            </ol>
          </CardContent>
        </Card>
        <Card className="bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Sources & destinations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              <b className="text-foreground">Rows (sources):</b> Cycling Env,
              the main Envelope, LFO 1 & 2, keyboard velocity/aftertouch, the
              keyboard itself, and the Wheel strip.
            </p>
            <p>
              <b className="text-foreground">Columns (destinations):</b> four
              hardwired (Pitch, Wave, Timbre, Cutoff) plus three{" "}
              <b className="text-foreground">Assign</b> columns you can point
              at nearly anything: hold an Assign button and turn the knob you
              want to control. With three pages of assigns, that's up to nine
              custom targets.
            </p>
            <p>
              Try first: LFO1 → Cutoff, small amount, slow sine. Then Velo/AT →
              Cutoff so your touch controls brightness.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ---- effects ---- */}
      <SectionHeading
        id="effects"
        icon={Sparkles}
        title="Effects — the polish"
        blurb={FX_ROUTING_NOTE}
      />
      <div className="mt-6 grid gap-2 md:grid-cols-2">
        {FX_TYPES.map((fx) => (
          <div
            key={fx.name}
            className="rounded-lg border border-border bg-card/70 p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold">{fx.name}</span>
              <Chip>{fx.use}</Chip>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{fx.desc}</p>
            <p className="mt-1.5 text-xs font-medium text-primary">{fx.knobs}</p>
          </div>
        ))}
      </div>
      <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
        A dependable starter chain: <b className="text-foreground">Chorus →
        Delay → Reverb</b>. Each effect's Subtypes (Shift+turn the Type
        encoder) are pre-cooked flavors — audition them before reaching for
        the knobs.
      </p>

      {/* ---- performance ---- */}
      <SectionHeading
        id="performance"
        icon={Sparkles}
        title="Playing it — expression tools"
        blurb="The MiniFreak is built to be played with two hands and then some. These are the tools that make a static preset feel alive under your fingers."
      />
      <div className="mt-6 grid gap-3 md:grid-cols-2">
        {[
          {
            t: "Touch strips (3 modes)",
            d: "The pads above them switch modes: white = classic pitch Bend + mod Wheel; blue = Macros M1/M2; orange = Gate & Spice for the arp/sequencer. Watch the LED colors to know where you are.",
          },
          {
            t: "The mod wheel does nothing… until you wire it",
            d: "The Wheel strip is just a modulation source — its own row at the bottom of the Matrix. Route Wheel → Cutoff and it becomes a brightness rider; point an Assign column at LFO1 AM and route Wheel → that, and the wheel fades a wobble in and out. Every preset can wire it differently.",
          },
          {
            t: "Macros — four knobs in one swipe",
            d: "Each preset stores two Macros, each moving up to 4 parameters by amounts you choose. Shift+touch the Macro pad, wiggle the knobs you want, set amounts, done. Macros are also a safe way to preview edits: slide back to 0 and the preset is untouched.",
          },
          {
            t: "Velocity & aftertouch",
            d: "The keys sense how fast you strike (velocity) and how hard you keep pressing (aftertouch). Both are Matrix sources — route them at Cutoff or Timbre and the synth responds to your touch. Curves are adjustable in Utility > Controls if the feel is off.",
          },
          {
            t: "Scales & Chord mode",
            d: "Shift+Chord locks the keyboard to a scale (wrong notes snap to right ones) — genuinely great while learning. Chord mode plays a stored chord from any single key; hold the Chord button and play an interval to reprogram it.",
          },
          {
            t: "Glide",
            d: "Pitch slides between notes instead of stepping. Small amounts sound vocal on mono leads; Sound Edit > Pitch > Glide Mode picks between fixed-time, fixed-rate, and legato-only variants.",
          },
          {
            t: "Vibrato",
            d: "A dedicated pitch-wobble LFO on the Wheel strip (Shift+touch the Bend/Wheel pad to enable). Set its rate and depth by holding the pad and using both strips.",
          },
        ].map((x) => (
          <Card key={x.t} className="bg-card/80">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{x.t}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              {x.d}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ---- arp & seq ---- */}
      <SectionHeading
        id="arp-seq"
        icon={Grid3X3}
        title="Arpeggiator & Sequencer"
        blurb="The row of touch buttons above the keys. The arp turns held notes into instant patterns; the sequencer records loops of up to 64 steps — including knob movements — and saves them with the preset."
      />
      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <Card className="bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Arp in 30 seconds</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Press <b className="text-foreground">Arp</b>, hold a chord, and
            it plays for you. Pads 1–8 pick the pattern, the Octave buttons
            stretch it over up to 4 octaves, and{" "}
            <b className="text-foreground">Hold</b> keeps it running
            hands-free (new notes get added to the held pile until you let
            everything go).
          </CardContent>
        </Card>
        <Card className="bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Sequencer in 30 seconds</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Press <b className="text-foreground">Seq</b>. With playback
            stopped, Record enters step mode; with playback running, Record
            captures your playing live. Up to 64 steps across 4 pages, six
            notes per step, one sequence saved per preset — tempo, swing,
            gate and all.
          </CardContent>
        </Card>
      </div>

      {/* -- arp deep dive -- */}
      <SubHeading
        title="Arp modes (pads 1–8)"
        blurb="Same held notes, eight different personalities."
      />
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {ARP_MODES.map((m) => (
          <div
            key={m.name}
            className="rounded-lg border border-border bg-card/70 p-3"
          >
            <p className="text-sm font-semibold">{m.name}</p>
            <p className="mt-1 text-sm text-muted-foreground">{m.desc}</p>
          </div>
        ))}
      </div>

      <SubHeading
        title="Arp modifiers — where the magic is"
        blurb="The pads to the right of the modes. Layer them onto any mode while it plays; this is where a held chord starts sounding like a performance."
      />
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {ARP_MODIFIERS.map((m) => (
          <div
            key={m.name}
            className="rounded-lg border border-border bg-card/70 p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">{m.name}</p>
              <Chip>{m.hold ? "hold to apply" : "toggle on/off"}</Chip>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{m.desc}</p>
          </div>
        ))}
      </div>
      <Card className="mt-3 border-primary/30 bg-card/80">
        <CardContent className="py-4 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">
            Caught something you love?
          </span>{" "}
          Long-press Record while the arp plays and the whole thing is copied
          into the sequencer, ready to edit step by step. (This replaces
          whatever sequence was there.)
        </CardContent>
      </Card>

      {/* -- seq deep dive -- */}
      <SubHeading
        title="Recording a sequence, step by step"
        blurb="Step recording is the precise way in — no timing pressure, you build the loop one step at a time."
      />
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <Card className="bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Step recording</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
              {SEQ_STEP_RECORDING.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          </CardContent>
        </Card>
        <Card className="bg-card/80">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Real-time recording & length
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              Prefer to just play? Hit Play, then Record, and perform — pitch,
              note length, and velocity are all captured as the loop cycles.
              Less precise, more human; you can always clean it up in step
              mode afterwards.
            </p>
            <p>
              <b className="text-foreground">Length:</b> hold the Last Step
              pad and tap a step (or a Page button) to set anywhere from 1 to
              64 steps. The page LEDs tell the story: red = the page you're
              viewing, dim white = pages inside the loop, blinking = the page
              currently playing.
            </p>
          </CardContent>
        </Card>
      </div>

      <SubHeading
        title="Editing tricks"
        blurb="A recorded sequence is clay, not stone."
      />
      <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
        {SEQ_EDIT_TRICKS.map((t) => (
          <div
            key={t.name}
            className="rounded-lg border border-border bg-card/70 p-3"
          >
            <p className="text-sm font-semibold">{t.name}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
          </div>
        ))}
      </div>

      <SubHeading
        title="The four modulation lanes"
        blurb="The sequencer's killer feature: it records knob moves, not just notes."
      />
      <Card className="mt-3 bg-card/80">
        <CardContent className="py-4 text-sm text-muted-foreground">
          {SEQ_MOD_LANES_NOTE}
        </CardContent>
      </Card>

      {/* -- shared groove tools -- */}
      <SubHeading
        title="The groove toolbox (shared by arp & seq)"
        blurb="Everything here is saved with the preset, so a sequence recalls with its whole feel intact."
      />
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {GROOVE_TOOLS.map((g) => (
          <div
            key={g.name}
            className="rounded-lg border border-border bg-card/70 p-3"
          >
            <p className="text-sm font-semibold">{g.name}</p>
            <p className="mt-1 text-sm text-muted-foreground">{g.desc}</p>
          </div>
        ))}
      </div>

      {/* ---- presets ---- */}
      <SectionHeading
        id="presets"
        icon={BookOpen}
        title="Presets — save everything, fear nothing"
        blurb="512 slots on board. A preset stores nearly everything: the sound, the sequence, the macros, the tempo. Browsing other people's presets and reverse-engineering them is a legitimate way to learn."
      />
      <div className="mt-6 grid gap-3 md:grid-cols-3">
        {[
          {
            t: "Browse & filter",
            d: "Turn the Preset encoder, click to load. Shift+turn filters by type (Bass, Pad, Lead…) so you're not scrolling all 512. An asterisk on screen = unsaved edits.",
          },
          {
            t: "Save early, save often",
            d: "Click Save, then click again to confirm — optionally choosing a new slot, type, and name along the way (click the Preset encoder to advance through those steps). No confirmation on overwrite, so watch the slot number.",
          },
          {
            t: "Escape hatches",
            d: "Long-press the Preset encoder = fresh init patch. Sound Edit > View Snapshots = time-machine back to earlier states of your edit. Utility > Preset Operations can copy, paste, or erase just parts of a preset.",
          },
        ].map((x) => (
          <Card key={x.t} className="bg-card/80">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{x.t}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              {x.d}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ---- recipes ---- */}
      <SectionHeading
        id="recipes"
        icon={Sparkles}
        title="First sounds — recipes"
        blurb="Five patches to build from scratch, each teaching a different part of the machine. Start every one by long-pressing the Preset encoder for an init patch."
      />
      <div className="mt-6 grid gap-3 md:grid-cols-2">
        {RECIPES.map((r) => (
          <RecipeCard key={r.name} recipe={r} />
        ))}
      </div>

      {/* ---- gotchas ---- */}
      <SectionHeading
        id="gotchas"
        icon={AlertTriangle}
        title="Gotchas & good-to-knows"
        blurb="Things that confuse everyone the first week."
      />
      <div className="mt-6 grid gap-2 md:grid-cols-2">
        {GOTCHAS.map((g) => (
          <div
            key={g.title}
            className="rounded-lg border border-amber-300/60 bg-amber-50/50 p-3 dark:border-amber-500/30 dark:bg-amber-500/5"
          >
            <p className="flex items-start gap-1.5 text-sm font-semibold">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              {g.title}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{g.body}</p>
          </div>
        ))}
      </div>

      {/* ---- glossary ---- */}
      <SectionHeading
        id="glossary"
        icon={BookOpen}
        title="Glossary"
        blurb="Sound-design words, minus the jargon."
      />
      <dl className="mt-6 grid gap-x-8 gap-y-3 md:grid-cols-2">
        {GLOSSARY.map(([term, def]) => (
          <div key={term} className="border-b border-border/60 pb-2">
            <dt className="text-sm font-semibold">{term}</dt>
            <dd className="mt-0.5 text-sm text-muted-foreground">{def}</dd>
          </div>
        ))}
      </dl>

      <footer className="mt-14 border-t border-border pt-5 text-center text-xs text-muted-foreground">
        Distilled from the Arturia MiniFreak User Manual (v1.0.0). When in
        doubt: hold a note and turn a knob — your ears are the real manual.
      </footer>
    </div>
  </div>
);

export default MiniFreak;
